import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ExtensionIcon from '@mui/icons-material/Extension';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function ClientIntegrations() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await axios.get(`${API_BASE}/integrations/bitrix`, {
        headers: authHeaders()
      });
      setStatus(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить статус интеграции');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const { data } = await axios.put(
        `${API_BASE}/integrations/bitrix`,
        { webhookUrl: webhookUrl.trim() },
        { headers: authHeaders() }
      );
      setStatus(data);
      setWebhookUrl('');
      setSuccess(data.message || 'Вебхук Битрикс успешно настроен');
    } catch (e) {
      setError(e.response?.data?.details || e.response?.data?.error || 'Не удалось сохранить вебхук');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setError('');
      setSuccess('');
      const { data } = await axios.post(
        `${API_BASE}/integrations/bitrix/test`,
        {},
        { headers: authHeaders() }
      );
      setStatus(data);
      setSuccess(data.message || 'Вебхук успешно проверен');
    } catch (e) {
      setError(e.response?.data?.details || e.response?.data?.error || 'Проверка не удалась');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      setError('');
      setSuccess('');
      const { data } = await axios.delete(`${API_BASE}/integrations/bitrix`, {
        headers: authHeaders()
      });
      setStatus({
        configured: false,
        webhookMasked: null,
        verifiedAt: null,
        lastError: null,
        usersScope: false
      });
      setSuccess(data.message || 'Интеграция отключена');
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось отключить интеграцию');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 0 }, py: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <ExtensionIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Интеграции
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>
          Битрикс24
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Подключите входящий вебхук Битрикс24, чтобы ответы на опросы автоматически создавали лиды или сделки.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Как создать вебхук
          </Typography>
          <Typography variant="body2" component="div">
            1. В Битрикс24 откройте <strong>Разработчикам → Другое → Входящий вебхук</strong>
            <br />
            2. Выдайте права: <strong>CRM</strong> (обязательно) и <strong>Пользователи (user)</strong> (для списка менеджеров)
            <br />
            3. Скопируйте URL вебхука и вставьте в поле ниже
            <br />
            4. После успешной проверки в конструкторе опросов появится шаг «Интеграции»
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Документация Битрикс24:{' '}
            <Link
              href="https://apidocs.bitrix24.ru/local-integrations/local-webhooks.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              входящие вебхуки
            </Link>
          </Typography>
        </Alert>

        {status?.configured ? (
          <Alert
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            severity="success"
            sx={{ mb: 3 }}
          >
            Вебхук Битрикс успешно настроен
            {status.webhookMasked ? ` (${status.webhookMasked})` : ''}.
            Теперь в конце создания опроса мы предложим настроить отправку ответов в Битрикс.
            {status.usersScope === false && (
              <>
                <br />
                <strong>Внимание:</strong> право «Пользователи» не подтверждено — список менеджеров может быть недоступен.
              </>
            )}
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Вебхук ещё не настроен. Без него шаг «Интеграции» в конструкторе опросов не появится.
            {status?.lastError ? (
              <>
                <br />
                Последняя ошибка: {status.lastError}
              </>
            ) : null}
          </Alert>
        )}

        <TextField
          fullWidth
          label="URL входящего вебхука Битрикс24"
          placeholder="https://ваш-портал.bitrix24.ru/rest/1/xxxxxxxx/"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          helperText={
            status?.webhookMasked
              ? `Текущий вебхук: ${status.webhookMasked}. Вставьте новый URL, чтобы заменить.`
              : 'Полный URL вида https://.../rest/.../'
          }
          sx={{ mb: 2 }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !webhookUrl.trim()}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Сохранить и проверить'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleTest}
            disabled={testing || !status?.webhookMasked}
          >
            {testing ? <CircularProgress size={22} /> : 'Проверить ещё раз'}
          </Button>
          <Button
            variant="text"
            color="error"
            onClick={handleDisconnect}
            disabled={disconnecting || !status?.configured}
          >
            {disconnecting ? <CircularProgress size={22} /> : 'Отключить'}
          </Button>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary">
          В Битрикс уходят ФИО, телефон и email респондента из аккаунта ThinkLink (если есть).
          Для публичных и внешних опросов контакты можно сопоставить с вопросами на шаге «Интеграции» в конструкторе.
          Заявки создаются при статусе ответа «completed» или после одобрения администратором (без повторов).
        </Typography>
      </Paper>
    </Box>
  );
}
